import { Link } from "react-router-dom";
import { ArrowRight, Trees, Mountain, Layers, Bird, Leaf, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import forestSection from "@/assets/forest/living-forest-section.jpg.asset.json";

const FOREST_METRICS = [
  { icon: Trees, label: "Expanding rainforest ecosystem" },
  { icon: Mountain, label: "Volcanic mountain rainforest" },
  { icon: Layers, label: "Three distinct forest zones" },
  { icon: Bird, label: "Indigenous & migratory wildlife" },
  { icon: Leaf, label: "Centuries of botanical knowledge" },
  { icon: Sparkles, label: "Living traditions still practiced today" },
];

/**
 * Homepage feature: "The Living Forest of St. Kitts" with a metrics band.
 * Routes to the Forest Heritage editorial hub.
 */
const ForestHeritageFeature = () => {
  return (
    <section className="relative overflow-hidden bg-[hsl(150_35%_8%)]">
      <div className="relative min-h-[70vh] flex items-center">
        <img
          src={forestSection.url}
          alt="Volcanic rainforest of St. Kitts at golden hour"
          loading="lazy"
          width={1600}
          height={1100}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, hsl(150 40% 5% / 0.92) 0%, hsl(150 40% 5% / 0.7) 55%, hsl(150 40% 5% / 0.3) 100%)",
          }}
        />
        <div
          className="relative z-10 container mx-auto px-6 py-24 md:py-32"
          style={{ maxWidth: "1400px" }}
        >
          <Reveal className="max-w-2xl">
            <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
              The Forest Island
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-[hsl(40_33%_96%)] leading-[1.05] mb-7">
              The Living Forest of St. Kitts
            </h2>
            <p className="font-sans text-base md:text-lg text-white/90 leading-relaxed mb-5 max-w-xl">
              For centuries, the forests of St. Kitts have provided food,
              medicine, shelter, fuel, and freedom. Their trees built homes.
              Their plants shaped traditions. Their mountain trails carried
              stories, knowledge, and resilience through generations.
            </p>
            <p className="font-sans text-base md:text-lg text-white/90 leading-relaxed mb-8 max-w-xl">
              Today, Sugar Town Organics preserves and reimagines that heritage
              through products, experiences, food, wellness, and education.
            </p>
            <p className="font-serif italic text-accent text-lg mb-10">
              Rooted Memory. Living Future.
            </p>
            <Link
              to="/forest-heritage"
              className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-sm font-sans font-semibold text-[11px] uppercase tracking-[0.28em] hover:opacity-90 transition-opacity"
            >
              Explore the Forest Heritage
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </div>

      {/* Forest metrics band */}
      <div className="bg-primary text-primary-foreground py-12 md:py-16 border-t border-primary-foreground/10">
        <div className="container mx-auto px-6">
          <p className="text-center font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-10">
            The Forest Island
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 max-w-5xl mx-auto">
            {FOREST_METRICS.map((m) => (
              <div key={m.label} className="flex items-center gap-4">
                <m.icon className="w-6 h-6 text-accent shrink-0" />
                <p className="font-serif text-base md:text-lg leading-tight">
                  {m.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForestHeritageFeature;
